import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  SCHOOL_YEAR_ACTIVE_ID,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  PermissionAction,
  StaffFilter as StaffFilterModel,
  StaffListFilter,
  StaffServiceFilter as StaffServiceFilterModel,
  StaffServiceListDto,
  StaffServiceListFilter,
  StaffServiceListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { StaffFilter } from "pages/staff/staff/StaffFilter";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { StaffServiceFilter } from "../StaffServiceFilter";
import { StaffServiceTable } from "./StaffServiceTable";
import { staffServicePageRoute } from "../StaffServicePage/StaffServicePage";

const routeName = "staffServices";

export const staffServiceListPageRoute: PageRoute = {
  handle: "Staff Services",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StaffServiceListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StaffService;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StaffListFilter>();
  const [initListFilter, setInitListFilter] = useState<StaffServiceListFilter>(listFilter);
  const [listItems, setListItems] = useState<StaffServiceListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StaffServiceListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.staffServices.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.staffServices.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    ApiService.staffServices.initListFilter().then((response) => {
      setListFilter(response);
      setInitListFilter(response);
      loadList(response);
    });

    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.staffServices.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StaffServiceListFilter>({
    maxWidth: "xl",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <Stack spacing="lg">
        <Stack spacing="lg">
          <Typography variant="h6" fontWeight={(theme) => theme.typography.fontWeightRegular}>
            Staff:
          </Typography>

          <StaffFilter
            filter={listFilter.staffFilter || ({} as StaffFilterModel)}
            setFilter={(filter) =>
              setListFilter(
                produce(listFilter, (draft) => {
                  draft.staffFilter = filter;
                })
              )
            }
          />
        </Stack>

        <Stack spacing="lg">
          <Typography variant="h6" fontWeight={(theme) => theme.typography.fontWeightRegular}>
            Service:
          </Typography>

          <StaffServiceFilter
            filter={listFilter.serviceFilter || ({} as StaffServiceFilterModel)}
            setFilter={(filter) =>
              setListFilter(
                produce(listFilter, (draft) => {
                  draft.serviceFilter = filter;
                })
              )
            }
          />
        </Stack>
      </Stack>
    ),
  });

  const getIdsMethods = () => ApiService.staffServices.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{staffServiceListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    navigate(staffServicePageRoute.getPath({ id: 0, crudMode: "edit", screenType: "standard" }))
                  }
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StaffServiceTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              staffServices={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
